import Cookies from "js-cookie"
import ApplicationComponent from "legacy/ApplicationComponent"
import AssignmentsFilterBar from "legacy/assignments-v2/AssignmentsFilterBar"
import AssignmentsReservationView from "legacy/assignments-v2/AssignmentsReservationView"
import AssignmentsView from "legacy/assignments-v2/AssignmentsView"
import AssignmentsWithoutSpaces from "legacy/assignments-v2/AssignmentsWithoutSpaces"
import MultiAssignmentView from "legacy/assignments-v2/MultiAssignmentView"
import PlaceholdersView from "legacy/assignments-v2/PlaceholdersView"
import UnassignedReservationView from "legacy/assignments-v2/UnassignedReservationView"
import {
  afterSetState,
  isPresent,
  updateUrlQueryParams,
} from "legacy/helpers.es6"
import MarinaMapView from "legacy/marina-map/MarinaMapView"
import moment from "moment"
import PropTypes from "prop-types"
import React from "react"
import clone from "rfdc/default"
import DockwalkSummary from "src/main/DockwalkSummary"

class AssignmentsV2Controller extends ApplicationComponent {
  constructor(props) {
    super(props)
    const start = moment(props.startDate, "YYYY-MM-DD")
    const end = moment(props.endDate, "YYYY-MM-DD")
    this.state = {
      visibleRange: this.getVisibleRange(start, end),
      spacesBySpaceGroups: props.spacesBySpaceGroups,
      assignmentsBySpace: props.assignmentsBySpace,
      spaceNotesBySpace: props.spaceNotesBySpace,
      placeholdersBySpace: props.placeholdersBySpace,
      spaceStatusesBySpace: props.spaceStatusesBySpace || {},
      unassignedReservationsCount: props.unassignedReservationsCount,
      selectedReservation: null,
      selectedReservationId: props.selectedReservationId,
      loadingReservation: false,
      loadingAvailability: true,
      viewType: props.viewType,
      transitioningViewType: props.viewType,
      sidebarView: null,
      visibleSpaces: "all",
      datePickerDate: start,
      loadingGrid: false,
      marinaMap: this.findMarinaMap(props.viewType),
      spacesWithGeometries: [],
      searchFormData: [],
      // Set viewType-related settings directly in constructor rather than using setState
      // This prevents "Can't call setState on a component that is not yet mounted" error
    }
    // Handle Cookie and URL updates from setViewType without using setState
    Cookies.set("viewType", props.viewType)
    updateUrlQueryParams({ viewType: props.viewType })
    // Track Amplitude event directly
    const viewTypeParts = (props.viewType || "").split(":")
    const viewTypeBase = viewTypeParts[0]

    switch (viewTypeBase) {
      case "map":
        Amplitude.trackEvent("assignments:switch_map")
        break
      case "monthly":
        Amplitude.trackEvent("assignments:switch_monthly")
        break
      case "weekly":
        Amplitude.trackEvent("assignments:switch_weekly")
        break
      case "dockwalk":
        Amplitude.trackEvent("assignments:switch_dockwalk")
        break
      case "dockwalkSummary":
        Amplitude.trackEvent("assignments:switch_dockwalk_summary")
    }
    // Handle date-related updates
    this.setDate(start)
    this.handleMarinaMapSpaceClick = this.handleMarinaMapSpaceClick.bind(this)
    this.loadReservationForSidebar = this.loadReservationForSidebar.bind(this)
    this.setPlaceholder = this.setPlaceholder.bind(this)
    this.openAddPlaceholder = this.openAddPlaceholder.bind(this)
    this.openAddNote = this.openAddNote.bind(this)
    this.openNoteForSpace = this.openNoteForSpace.bind(this)
    this.openUnassignedReservations = this.openUnassignedReservations.bind(this)
    this.addSpaceStatus = this.addSpaceStatus.bind(this)
    this.reloadCurrentGrid = this.reloadCurrentGrid.bind(this)
    this.closeSidebar = this.closeSidebar.bind(this)
    this.dateChange = this.dateChange.bind(this)
    this.fetchRemainingLinearDockage =
      this.fetchRemainingLinearDockage.bind(this)
    this.setViewType = this.setViewType.bind(this)
  }

  componentDidMount() {
    this.fetchRemainingLinearDockage()
    if (isPresent(this.props.selectedReservationId)) {
      this.loadReservationForSidebar(this.props.selectedReservationId)
    }
    if (isPresent(this.state.marinaMap)) {
      this.fetchSpaceGeometries()
    }
    this.setState({
      searchFormData: this.spacesWithAllData(this.props.spacesForDockwalk),
    })
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.marinaMap !== prevState.marinaMap &&
      isPresent(this.state.marinaMap)
    ) {
      this.fetchSpaceGeometries()
    }
    if (
      this.state.viewType !== prevState.viewType ||
      this.state.assignmentsBySpace !== prevState.assignmentsBySpace ||
      this.state.placeholdersBySpace !== prevState.placeholdersBySpace ||
      this.state.spaceNotesBySpace !== prevState.spaceNotesBySpace
    ) {
      this.setState({
        searchFormData: this.spacesWithAllData(this.props.spacesForDockwalk),
      })
    }
  }

  findMarinaMap(viewType) {
    if (!viewType) return null

    const viewTypeParts = viewType.split(":")
    if (viewTypeParts.length < 2) return null

    const marinaMapId = parseInt(viewTypeParts[1])
    return this.props.marinaMaps.find((map) => map.id === marinaMapId)
  }

  isMarinaMapView() {
    return this.state.viewType.includes("map")
  }

  viewTypeInDays() {
    const viewTypeParts = (this.state.viewType || "").split(":")
    const viewTypeBase = viewTypeParts[0]

    switch (viewTypeBase) {
      case "dockwalk":
      case "dockwalkSummary":
      case "map":
        return 1
      case "weekly":
        return 7
      case "monthly":
        return 28
      default:
        return 1
    }
  }

  getVisibleRange(start, end) {
    const days = end.diff(start, "days") + 1
    const daysArray = Array(days)
      .fill()
      .map((_, i) => start.clone().add(i, "days").format("YYYY-MM-DD"))
    const dayBeforeStart = start
      .clone()
      .subtract(1, "days")
      .format("YYYY-MM-DD")
    const dayAfterEnd = end.clone().add(1, "days").format("YYYY-MM-DD")
    return {
      start,
      end,
      dayBeforeStart,
      dayAfterEnd,
      days,
      daysArray,
    }
  }

  spacesWithAllData(spaces) {
    if (!spaces || !Array.isArray(spaces)) return []

    const { assignmentsBySpace, placeholdersBySpace, spaceNotesBySpace } =
      this.state
    const spaceIds = spaces.map((space) => space.id)

    return spaceIds.map((id) => {
      const space = spaces.find((s) => s.id === id)
      const assignments = assignmentsBySpace[id] || []
      const placeholders = placeholdersBySpace[id] || []
      const spaceNotes = spaceNotesBySpace[id] || []
      return {
        ...space,
        name: space.name,
        assignments,
        placeholders,
        spaceNotes,
      }
    })
  }

  mergeSpaceData(spaces, spaceGeometries) {
    if (
      !spaces ||
      !Array.isArray(spaces) ||
      !spaceGeometries ||
      !Array.isArray(spaceGeometries)
    ) {
      return []
    }

    const spaceIds = spaces.map((space) => space.id)
    const spaceGeometrySpaceIds = spaceGeometries.map((geo) => geo.space_id)

    // Deep clone objects to avoid mutation using rfdc
    const assignmentsBySpaceCopy = clone(this.state.assignmentsBySpace || {})
    const placeholdersBySpaceCopy = clone(this.state.placeholdersBySpace || {})

    const filteredAssignmentsBySpace = this.filterMapSpaceObjects(
      assignmentsBySpaceCopy,
      this.state.visibleRange.start
    )

    const filteredPlaceholdersBySpace = this.filterMapSpaceObjects(
      placeholdersBySpaceCopy,
      this.state.visibleRange.start
    )

    return spaceGeometrySpaceIds.map((id) => {
      let newSpaceGeo = null
      if (spaceIds.includes(id)) {
        const space = spaces.find((s) => s.id === id)
        // we need space group data for assignment popovers
        const spaceGroup = this.props.spacesBySpaceGroups.find(
          (group) =>
            group.id && group.spaces && group.spaces.find((s) => s.id === id)
        )
        const geometry = spaceGeometries.find((geo) => geo.space_id === id)
        newSpaceGeo = {
          ...geometry,
          name: space.name,
          assignments: filteredAssignmentsBySpace[id] || [],
          placeholders: filteredPlaceholdersBySpace[id],
          spaceGroup,
        }
      }
      return newSpaceGeo
    })
  }

  filterMapSpaceObjects(objectsBySpace, mapDate) {
    const result = { ...objectsBySpace }

    for (const spaceId in result) {
      result[spaceId] = (result[spaceId] || []).filter((obj) => {
        const objectEndNight = obj.end_night
          ? moment(obj.end_night)
          : moment(obj.end_date)
        const endsAfterMapDate = objectEndNight >= mapDate

        const mapDateString = mapDate.format("YYYY-MM-DD")
        let isDayTrip
        if (obj.check_in_date) {
          isDayTrip =
            objectEndNight.check_in_date === mapDateString &&
            obj.check_out_date === mapDateString
        } else {
          isDayTrip =
            obj.start_date === mapDateString && obj.end_date === mapDateString
        }

        return endsAfterMapDate || isDayTrip
      })
    }
    return result
  }

  fetchSpaceGeometries() {
    this.setState({
      loadingMap: true,
    })
    $.get(
      this.state.marinaMap.getSpaceGeometriesPath,
      { uuid: this.state.marinaMap.uuid },
      (data) => {
        this.setState({
          loadingMap: false,
          spacesWithGeometries: this.mergeSpaceData(
            this.props.spacesForDockwalk,
            data.spaceGeometries
          ),
        })
      }
    )
  }

  fetchRemainingLinearDockage() {
    const loaV2SpaceGroups = (this.props.spaceGroups || []).filter(
      (spaceGroup) => spaceGroup.type === "SpaceGroup::LinearV2"
    )
    const start = this.state.visibleRange.start
    const end = this.state.visibleRange.end
    if (
      this.state.viewType === "weekly" &&
      loaV2SpaceGroups.length > 0 &&
      start &&
      end
    ) {
      const params = {
        start_date: start.format("YYYY-MM-DD"),
        end_date: end.format("YYYY-MM-DD"),
      }
      $.get(this.props.remainingLinearDockageRoute, params, (data) => {
        this.setState({
          loaV2Availability: data,
          loadingAvailability: false,
        })
      }).fail(() => {
        this.setState({
          loadingErrorMessage: "There was an issue calculating availability",
          loadingAvailability: false,
        })
      })
    } else {
      this.setState({ loadingAvailability: false })
    }
  }

  handleMarinaMapSpaceClick(space, objects) {
    Amplitude.trackEvent("assignments:clicked_map_space")
    if (objects.length === 1) {
      objects[0].type === "assignment"
        ? this.loadReservationForSidebar(objects[0].reservation_id, space)
        : this.setPlaceholder(objects[0], space)
    } else {
      this.setSidebar({
        sidebarView: "multiReservation",
        comingFromUnassigned: this.state.comingFromUnassigned,
        selectedSpace: space,
      })
    }
  }

  loadReservationForSidebar(id, space, { skipScroll } = {}) {
    Amplitude.trackEvent("assignments:open_assign", { reservation_id: id })
    this.setSidebar({
      sidebarView: "loadingReservation",
      selectedReservationId: id,
      comingFromUnassigned: this.state.comingFromUnassigned,
      loadingReservation: true,
      selectedSpace: space,
    })
    if (!skipScroll) {
      const selectedAssignment = document.getElementById(id)
      if (selectedAssignment) {
        const elementTop =
          selectedAssignment.getBoundingClientRect().top + window.scrollY
        const offset = 190
        const scrollOptions = { top: elementTop - offset, behavior: "smooth" }
        window.scrollTo(scrollOptions)
      }
    }
    const reservationSidebarRequest = $.get(
      `${this.props.reservationEndpoint}/${id}/assignments_v2`,
      (reservation) => {
        this.setSidebar({
          sidebarView: "reservation",
          selectedReservation: reservation,
          comingFromUnassigned: this.state.comingFromUnassigned,
          loadingReservation: false,
          selectedSpace: space,
        })
      }
    )
    this.setState({ reservationSidebarRequest })
  }

  setViewType(viewType, callback = this.reloadCurrentGrid) {
    Cookies.set("viewType", viewType)
    updateUrlQueryParams({ viewType })

    const viewTypeParts = (viewType || "").split(":")
    const viewTypeBase = viewTypeParts[0]

    switch (viewTypeBase) {
      case "map":
        Amplitude.trackEvent("assignments:switch_map")
        break
      case "monthly":
        Amplitude.trackEvent("assignments:switch_monthly")
        break
      case "weekly":
        Amplitude.trackEvent("assignments:switch_weekly")
        break
      case "dockwalk":
        Amplitude.trackEvent("assignments:switch_dockwalk")
        break
      case "dockwalkSummary":
        Amplitude.trackEvent("assignments:switch_dockwalk_summary")
    }
    this.abortExistingRequest()
    this.setState(
      {
        transitioningViewType: viewType,
        sidebarView: null,
        selectedPlaceholder: null,
        selectedReservation: null,
        selectedReservationId: null,
        selectedSpace: null,
        reservationSidebarRequest: null,
      },
      callback
    )
  }

  setDate(date) {
    updateUrlQueryParams({ night: date.format("YYYY-MM-DD") })
  }

  dateChange(date) {
    this.setState({ loadingGrid: true, loadingAvailability: true })
    this.setDate(date)
    this.loadGridDataForDate(date.format("YYYY-MM-DD"), (gridData) => {
      this.setGridDataToMainView(gridData)
    })
  }

  reloadCurrentGrid(completion = () => {}) {
    this.setState({ loadingGrid: true, loadingAvailability: true })
    this.loadGridDataForDate(
      this.state.visibleRange.start.format("YYYY-MM-DD"),
      (gridData) => {
        this.setGridDataToMainView(gridData)
        completion()
      }
    )
  }

  loadGridDataForDate(night, completion) {
    $.get(
      $context.gridDataEndpoint,
      { night, view_type: this.state.transitioningViewType },
      (gridData) => completion(gridData)
    )
    afterSetState(() => $(".popover").remove())
  }

  setGridDataToMainView(gridData) {
    if (gridData === null) return null
    const start = moment(gridData.startDate, "YYYY-MM-DD")
    const end = moment(gridData.endDate, "YYYY-MM-DD")
    const viewType = this.state.transitioningViewType
    const marinaMap = this.findMarinaMap(viewType)
    this.setState(
      {
        assignmentsBySpace: gridData.assignmentsBySpace,
        spaceNotesBySpace: gridData.spaceNotesBySpace,
        placeholdersBySpace: gridData.placeholdersBySpace,
        spaceStatusesBySpace: gridData.spaceStatusesBySpace || {},
        unassignedReservationsCount: gridData.unassignedReservationsCount,
        visibleRange: this.getVisibleRange(start, end),
        datePickerDate: start,
        loadingGrid: false,
        marinaMap,
        viewType,
        transitioningViewType: viewType,
      },
      () => {
        this.fetchRemainingLinearDockage()
        if (isPresent(marinaMap)) {
          this.fetchSpaceGeometries()
        }
      }
    )
  }

  setPlaceholder(placeholder, space, { skipScroll } = {}) {
    if (placeholder.type === "note") {
      Amplitude.trackEvent("assignments:open_note")
    } else {
      Amplitude.trackEvent("assignments:open_placeholder")
    }
    this.setSidebar({
      sidebarView: placeholder.type,
      selectedPlaceholder: placeholder,
      selectedSpace: space,
    })
    if (!skipScroll) {
      const placeholderElement = document.getElementById(
        `${placeholder.type}-${placeholder.id}`
      )
      if (placeholderElement) {
        const elementTop =
          placeholderElement.getBoundingClientRect().top + window.scrollY
        const offset = 190
        const scrollOptions = { top: elementTop - offset, behavior: "smooth" }
        window.scrollTo(scrollOptions)
      }
    }
  }

  openAddPlaceholder(event) {
    if (event) event.preventDefault()
    if (this.state.sidebarView === "placeholder") return this.closeSidebar()

    const start = this.state.visibleRange.start.format("YYYY-MM-DD")
    this.setSidebar({
      sidebarView: "placeholder",
      selectedPlaceholder: {
        id: null,
        type: "placeholder",
        start_date: start,
        end_date: start,
      },
    })
  }

  openAddNote(event) {
    if (event) event.preventDefault()
    if (this.state.sidebarView === "note") return this.closeSidebar()

    const start = this.state.visibleRange.start.format("YYYY-MM-DD")
    this.setSidebar({
      sidebarView: "note",
      selectedPlaceholder: {
        id: null,
        type: "note",
        start_date: start,
        end_date: start,
      },
    })
  }

  openNoteForSpace(spaceId) {
    const start = this.state.visibleRange.start.format("YYYY-MM-DD")
    this.setSidebar({
      sidebarView: "note",
      selectedPlaceholder: {
        id: null,
        type: "note",
        start_date: start,
        end_date: start,
        space_id: spaceId,
        is_flagged: true,
      },
    })
  }

  openUnassignedReservations(event) {
    if (event) event.preventDefault()
    if (this.state.sidebarView === "unassigned") return this.closeSidebar()

    this.setSidebar({ sidebarView: "unassigned", comingFromUnassigned: true })
  }

  closeSidebar() {
    this.setSidebar()
  }

  abortExistingRequest() {
    if (this.state.reservationSidebarRequest) {
      this.state.reservationSidebarRequest.abort()
    }
  }

  setSidebar(extraState = {}) {
    this.abortExistingRequest()
    this.setState({
      sidebarView: null,
      selectedPlaceholder: null,
      selectedReservation: null,
      selectedReservationId: null,
      selectedSpace: null,
      comingFromUnassigned: false,
      comingFromMapView: this.state.viewType.includes("map"),
      reservationSidebarRequest: null,
      ...extraState,
    })
  }

  addSpaceStatus(spaceId, status) {
    switch (status) {
      case "vacant":
        Amplitude.trackEvent("assignments:mark_vacant")
        break
      case "occupied":
        Amplitude.trackEvent("assignments:mark_occupied")
    }
    this.setState({ loadingGrid: true })
    const newStatus = {
      status,
      created_at: moment(),
    }
    const spaceStatusesCopy = { ...this.state.spaceStatusesBySpace }
    spaceStatusesCopy[spaceId] = [
      ...(spaceStatusesCopy[spaceId] || []),
      newStatus,
    ]
    this.setState({ spaceStatusesBySpace: spaceStatusesCopy })
    $.post(
      $context.spaceStatusCreateEndpoint,
      {
        space_id: spaceId,
        status,
        date: this.state.visibleRange.start.format("YYYY-MM-DD"),
      },
      () => {
        this.setState({ loadingGrid: false })
      }
    )
  }

  renderSidePanelContents() {
    switch (this.state.sidebarView) {
      case "loadingReservation":
        return (
          <div
            className="flex flex-col items-center justify-center"
            style={{ height: 100 }}
          >
            <i className="text-muted fa-solid fa-spinner-third fa-spin" />
            <span>Loading Reservation...</span>
          </div>
        )
      case "multiReservation":
        return (
          <MultiAssignmentView
            loadReservationForSidebar={this.loadReservationForSidebar}
            selectedSpace={this.state.selectedSpace}
            setPlaceholder={this.setPlaceholder}
          />
        )
      case "reservation":
        return (
          <AssignmentsReservationView
            closeSidebar={this.closeSidebar}
            comingFromMapView={this.state.comingFromMapView}
            comingFromUnassigned={this.state.comingFromUnassigned}
            hasLinearDockageEnabled={$context.hasLinearDockageEnabled}
            marina={this.props.marina}
            openUnassignedReservations={this.openUnassignedReservations}
            reloadCurrentGrid={this.reloadCurrentGrid}
            reservationEndpoint={this.props.reservationEndpoint}
            reservationLabels={this.props.reservationLabels}
            selectedReservation={this.state.selectedReservation}
            spacesBySpaceGroups={this.state.spacesBySpaceGroups}
          />
        )
      case "note":
      case "placeholder":
        return (
          <PlaceholdersView
            closeSidebar={this.closeSidebar}
            comingFromMapView={this.state.comingFromMapView}
            reloadCurrentGrid={this.reloadCurrentGrid}
            selectedPlaceholder={this.state.selectedPlaceholder}
            showFlagToggle={this.state.viewType === "dockwalk"}
            spacesBySpaceGroups={this.state.spacesBySpaceGroups}
          />
        )
      case "unassigned":
        return (
          <UnassignedReservationView
            loadReservationForSidebar={this.loadReservationForSidebar}
            night={this.state.visibleRange.start}
            unassignedReservationsEndpoint={
              this.props.unassignedReservationsEndpoint
            }
          />
        )
    }
  }

  renderSidebar() {
    if (this.state.sidebarView) {
      return (
        <div
          className="fixed inset-0 z-50 flex flex-shrink-0 flex-grow-0 justify-center bg-black/50 xl:sticky xl:inset-auto xl:top-18 xl:z-30 xl:bg-transparent"
          style={{ flexBasis: 400 }}
          onClick={this.closeSidebar}
        >
          <div
            className={`card fixed bottom-0 top-0 flex w-full justify-center xl:relative xl:block ${
              ["unassigned"].includes(this.state.sidebarView)
                ? "max-w-6xl"
                : "max-w-2xl"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="close sidebar"
              className="absolute right-0 top-0 z-10 mr-2 mt-2 cursor-pointer border-none bg-transparent"
              onClick={this.closeSidebar}
            >
              <i className="fa-solid fa-xmark p-3 text-sm" />
            </button>
            {this.renderSidePanelContents()}
          </div>
        </div>
      )
    } else {
      return null
    }
  }

  render() {
    if (this.state.spacesBySpaceGroups.length > 0) {
      return (
        <div>
          <AssignmentsFilterBar
            dateChange={this.dateChange}
            datePickerDate={this.state.datePickerDate}
            handleMarinaMapSpaceClick={this.handleMarinaMapSpaceClick}
            loadingGrid={this.state.loadingGrid}
            marinaMaps={this.props.marinaMaps}
            meteredElectricPath={this.props.meteredElectricPath}
            onChange={this.handleInputChange}
            openAddPlaceholder={this.openAddPlaceholder}
            openAddNote={this.openAddNote}
            openUnassignedReservations={this.openUnassignedReservations}
            setViewType={this.setViewType}
            sidebarView={this.state.sidebarView}
            unassignedReservationsCount={this.state.unassignedReservationsCount}
            viewType={this.state.viewType}
            visibleSpaces={this.state.visibleSpaces}
          />
          <div className="flex items-start">
            {this.isMarinaMapView() ? (
              <div className="mx-3 flex-grow">
                <MarinaMapView
                  date={this.state.visibleRange.start}
                  dateChange={this.dateChange}
                  handleMarinaMapSpaceClick={this.handleMarinaMapSpaceClick}
                  loadingMap={this.state.loadingMap}
                  mapAssignmentView={true}
                  mapId={this.state.marinaMap.id}
                  mapImage={this.state.marinaMap.image}
                  reservationLabelsEnabled={this.props.reservationLabelsEnabled}
                  spacesWithGeometries={this.state.spacesWithGeometries}
                  searchFormData={this.state.spacesWithGeometries}
                  selectedReservation={this.state.selectedReservation}
                  selectedReservationId={this.state.selectedReservationId}
                  selectedSpace={this.state.selectedSpace}
                  loadReservationForSidebar={this.loadReservationForSidebar}
                  setPlaceholder={this.setPlaceholder}
                  viewType={this.state.viewType}
                  visibleRange={this.state.visibleRange}
                />
              </div>
            ) : (
              <div className="card mx-3 min-w-0 flex-grow border-b-0 border-t-0 p-0">
                {this.state.viewType === "dockwalkSummary" ? (
                  <DockwalkSummary
                    marinaSlug={this.props.marina.slug}
                    date={this.state.visibleRange.start.format("YYYY-MM-DD")}
                  />
                ) : (
                  <AssignmentsView
                    addSpaceStatus={this.addSpaceStatus}
                    assignmentsBySpace={this.state.assignmentsBySpace}
                    closeSidebar={this.closeSidebar}
                    dateChange={this.dateChange}
                    loadingAvailability={this.state.loadingAvailability}
                    loadReservationForSidebar={this.loadReservationForSidebar}
                    loaV2Availability={this.state.loaV2Availability}
                    placeholdersBySpace={this.state.placeholdersBySpace}
                    searchFormData={this.state.searchFormData}
                    selectedPlaceholder={this.state.selectedPlaceholder}
                    selectedReservationId={this.state.selectedReservationId}
                    setPlaceholder={this.setPlaceholder}
                    spacesBySpaceGroups={this.state.spacesBySpaceGroups}
                    spacesForDockwalk={this.props.spacesForDockwalk}
                    spaceNotesBySpace={this.state.spaceNotesBySpace}
                    spaceStatusesBySpace={this.state.spaceStatusesBySpace}
                    viewType={this.state.viewType}
                    viewTypeInDays={this.viewTypeInDays()}
                    visibleRange={this.state.visibleRange}
                    visibleSpaces={this.state.visibleSpaces}
                    openUnassignedReservations={this.openUnassignedReservations}
                    openAddPlaceholder={this.openAddPlaceholder}
                    openAddNote={this.openAddNote}
                    openNoteForSpace={this.openNoteForSpace}
                  />
                )}
              </div>
            )}
            {this.renderSidebar()}
          </div>
        </div>
      )
    } else {
      return <AssignmentsWithoutSpaces marinaSlug={this.props.marina.slug} />
    }
  }
}

AssignmentsV2Controller.propTypes = {
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  spacesBySpaceGroups: PropTypes.array.isRequired,
  assignmentsBySpace: PropTypes.object.isRequired,
  spaceNotesBySpace: PropTypes.object.isRequired,
  placeholdersBySpace: PropTypes.object.isRequired,
  spaceStatusesBySpace: PropTypes.object,
  unassignedReservationsCount: PropTypes.number.isRequired,
  selectedReservationId: PropTypes.string,
  viewType: PropTypes.string.isRequired,
  marinaMaps: PropTypes.array.isRequired,
  spacesForDockwalk: PropTypes.array.isRequired,
  spaceGroups: PropTypes.array.isRequired,
  remainingLinearDockageRoute: PropTypes.string.isRequired,
  reservationEndpoint: PropTypes.string.isRequired,
  meteredElectricPath: PropTypes.string,
  marina: PropTypes.object.isRequired,
  reservationLabels: PropTypes.array,
  reservationLabelsEnabled: PropTypes.bool,
  unassignedReservationsEndpoint: PropTypes.string.isRequired,
}

export default AssignmentsV2Controller
